import { useTranslations } from "use-intl";
import { useState } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

type Point = {
  date: string;
  label: string;
  weight: number;
  oneRm: number;
  volume: number;
  seconds: number;
  minutes: number;
};

type Metric = "weight" | "oneRm" | "volume" | "seconds" | "minutes";

/** One series at a time, so there is a single y-axis and no legend to decode. */
export function ProgressChart({ type, points }: { type: "reps" | "time" | "cardio"; points: Point[] }) {
  const t = useTranslations("progress");
  const metrics: { key: Metric; label: string; unit: string }[] =
    type === "reps"
      ? [
          { key: "weight", label: t("chartWeight"), unit: "kg" },
          { key: "oneRm", label: "1RM", unit: "kg" },
          { key: "volume", label: t("chartVolume"), unit: "kg" },
        ]
      : type === "time"
        ? [{ key: "seconds", label: t("chartTime"), unit: "s" }]
        : [{ key: "minutes", label: t("chartTime"), unit: "min" }];
  const [metric, setMetric] = useState<Metric>(metrics[0].key);
  const current = metrics.find((m) => m.key === metric) ?? metrics[0];
  const config = { [current.key]: { label: `${current.label} (${current.unit})`, color: "var(--chart-1)" } } satisfies ChartConfig;

  return (
    <div className="mt-5 rounded-2xl border bg-card p-3">
      <div className="mb-2 flex items-center justify-between gap-2 px-1">
        <p className="text-sm font-semibold">
          {current.label} <span className="font-normal text-muted-foreground">({current.unit})</span>
        </p>
        {metrics.length > 1 && (
          <ToggleGroup type="single" size="sm" variant="outline" value={metric} onValueChange={(v) => v && setMetric(v as Metric)}>
            {metrics.map((m) => (
              <ToggleGroupItem key={m.key} value={m.key} className="px-2 text-xs">
                {m.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        )}
      </div>
      <ChartContainer config={config} className="aspect-[4/3] w-full">
        <LineChart data={points} margin={{ top: 12, right: 12, bottom: 0, left: -12 }}>
          <CartesianGrid vertical={false} strokeOpacity={0.5} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} />
          <YAxis tickLine={false} axisLine={false} width={44} domain={["auto", "auto"]} />
          <ChartTooltip cursor={{ strokeDasharray: "3 3" }} content={<ChartTooltipContent />} />
          <Line
            dataKey={current.key}
            type="monotone"
            stroke={`var(--color-${current.key})`}
            strokeWidth={2}
            dot={{ r: 4, strokeWidth: 2, fill: "var(--card)" }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ChartContainer>
    </div>
  );
}
